import {messages} from '@smartmixin/i18n';
import React from 'react';
import isEqual from 'react-fast-compare';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {UserService, WeatherService} from '../api';
import {
  addFavoriteStation,
  addStation,
  editMyWeatherOrder,
  getDebugEmail,
  removeFavoriteLocation,
  removeFavoriteStation,
  reorderFavoriteLocations,
  restoreFavoriteLocation,
  selectFavoriteStationIds,
  selectMyWeatherEditorSections,
  selectMyWeatherOrder,
  selectMyWeatherOrderStationIds,
  selectMyWeatherSyncEnabled,
  setAlert,
  setMyWeatherOrder,
  setStationSort,
} from '../store';
import {CommonRootState} from '../store/types';
import {MyWeather, Station} from '../utils';
import {trackCoreEvent} from '../utils/Analytics/sink';
import useApiClient, {ApiError, sameUser} from './useApiClient';

/**
 * The tail of each station's chain of favourite writes (#419): a removal, or
 * Undo's add, goes once the one before it has answered, or the server could
 * take them in another order. At module level, so the chain outlives the
 * editor: Undo is pressed on the snackbar, maybe after the editor has
 * closed, and another editor can open meanwhile. A tail never rejects; the
 * entry goes once its chain has settled.
 */
const stationWrites = new Map<number, Promise<void>>();

/**
 * The station orders sent on their own (#419), one at a time: the tail of
 * their chain, and the number of the latest move queued. At module level
 * too: the overlay unmounts the editor on each return to the list, and a
 * move made in the next one must wait for one still out.
 */
const stationOrderWrites = {
  tail: Promise.resolve() as Promise<void>,
  latest: 0,
};

/** Forget every chain, for tests. */
export const clearStationWrites = () => {
  stationWrites.clear();
  // `latest` keeps counting: a move a test left waiting stays outdated.
  stationOrderWrites.tail = Promise.resolve();
};

/**
 * `write` for `stationId`, after its writes already queued. Sent at once
 * when none is, as before.
 */
const queueStationWrite = <T,>(
  stationId: number,
  write: () => Promise<T>,
): Promise<T> => {
  const previous = stationWrites.get(stationId);
  const next = previous ? previous.then(write) : write();
  const tail = next.then(
    () => {},
    () => {},
  );
  stationWrites.set(stationId, tail);
  tail.then(() => {
    if (stationWrites.get(stationId) === tail) {
      stationWrites.delete(stationId);
    }
  });
  return next;
};

/**
 * Whether the session is still the one signed in when it was taken: a
 * failure or a write coming up after a sign-out, an account switch or the
 * end of Login As is not about what was shown then. The user, not the email:
 * under Login As a /me writes the viewed account's there. Signed out stays
 * the same session while nobody signs in.
 */
const sessionOf = (store: {getState: () => CommonRootState}) => {
  const sentBy = store.getState().user;
  return () => {
    const {user} = store.getState();
    if (sentBy.token == null) {
      return user.token == null;
    }
    return (
      user.token != null &&
      sameUser(user.token, sentBy.token) &&
      user.debugEmail === sentBy.debugEmail
    );
  };
};

/**
 * Where a token sat in the My weather order: the token just before it, and
 * its index. None when the order did not name it (or there was no order yet,
 * before the first /me): Undo then puts it at the end, as before.
 */
type Slot = {after: string | null; index: number};

const slotOf = (order: string[] | null, token: string): Slot | null => {
  const index = order ? order.indexOf(token) : -1;
  if (!order || index === -1) {
    return null;
  }
  return {after: index > 0 ? order[index - 1] : null, index};
};

/**
 * `order` with `token` back in its `slot`: after the token it followed, or at
 * its index if that one is gone too (the end, if the order is shorter now).
 * An order that still names it is left as it is, a move made since included.
 */
const putBack = (
  order: string[],
  token: string,
  slot: Slot | null,
): string[] => {
  if (!slot || order.includes(token)) {
    return order;
  }
  const after = slot.after === null ? -1 : order.indexOf(slot.after);
  const at = after === -1 ? slot.index : after + 1;
  return [...order.slice(0, at), token, ...order.slice(at)];
};

/**
 * What the My weather editor changes (FE #381 step 3, design R1), for both
 * apps. Every change applies at once: here, then on the account.
 *
 * - A move in Favorites goes into the My weather order, the places between
 *   the stations keeping their slots, and into the station order;
 *   useMyWeatherOrderSync sends both in one request.
 * - A move in My stations changes the station order only, sent on its own:
 *   with it, this device's copy of the order could overwrite a newer one
 *   from another device. An edit still waiting to be sent takes it instead.
 * - Remove takes a favourite out at once and offers Undo, which puts it back
 *   where it was. Only the favourite goes: the station stays as a recent one,
 *   with its dashboard and forecast settings, as the list's star does. A
 *   station's removal that fails puts it back, and says so.
 * - Hide and Show again wait for the account, then say what they did.
 * - Under Login As the editor is not offered (selectMyWeatherEditable): the
 *   list is another account's, while the places and the My weather order on
 *   this device are the staff member's own. Should a change still come in,
 *   those are left alone and nothing is left pending to go later to the
 *   staff member's account: a move sends the station order only, to the
 *   account being viewed, as the cog screen always did, and Remove on a
 *   place does nothing (#420).
 */
const useMyWeatherEditor = () => {
  const {fetch} = useApiClient();
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  // Read at the moment of each change, not subscribed to: only the groups
  // need to re-render anyone.
  const store = useStore<CommonRootState>();
  const sections = useSelector(selectMyWeatherEditorSections);

  // A move waiting when a newer one is queued is not sent: the newer one
  // carries it. No timer, which would drop a move made just before the
  // editor closes.
  const sendStationIds = React.useCallback(
    (stationIds: number[]) => {
      // Signed out the station order is this device's only (1.65: My
      // weather is everyone's): there is no account to send it to, and the
      // refusal would show as an error.
      if (store.getState().user.token == null) {
        return;
      }
      const writes = stationOrderWrites;
      const move = ++writes.latest;
      const sameSession = sessionOf(store);
      writes.tail = writes.tail
        .then(async () => {
          if (move !== writes.latest || !sameSession()) {
            return;
          }
          const answer = await fetch(
            UserService.updateUserProfile({station_ids: stationIds}),
          );
          // Only the latest move's answer, and only while the device still
          // shows what was sent: an older one would undo the moves made
          // since, a Favorites move included (it changes the station order
          // without this chain). The account stores the ids sent as they
          // are, so this drops nothing it added.
          if (
            move === writes.latest &&
            sameSession() &&
            isEqual(store.getState().weather.stationSort, stationIds) &&
            Array.isArray(answer?.station_ids)
          ) {
            dispatch(setStationSort(answer.station_ids));
          }
        })
        .catch(() => {});
    },
    [dispatch, fetch, store],
  );

  /**
   * A new My weather order, and the station order it goes with. Pending, to
   * go together, only while the order sync can send it (#421).
   */
  const saveOrder = React.useCallback(
    (order: string[], stationIds: number[]) => {
      dispatch(setStationSort(stationIds));
      if (selectMyWeatherSyncEnabled(store.getState())) {
        dispatch(editMyWeatherOrder({order, stationIds}));
        return;
      }
      dispatch(setMyWeatherOrder(order));
      sendStationIds(stationIds);
    },
    [dispatch, sendStationIds, store],
  );

  /** A new station order only. */
  const saveStationOrder = React.useCallback(
    (stationIds: number[]) => {
      dispatch(setStationSort(stationIds));
      const state = store.getState();
      const {order, orderPending} = state.myWeather;
      if (
        order &&
        orderPending &&
        selectMyWeatherSyncEnabled(state) &&
        !getDebugEmail(state)
      ) {
        dispatch(editMyWeatherOrder({order, stationIds}));
        return;
      }
      sendStationIds(stationIds);
    },
    [dispatch, sendStationIds, store],
  );

  /** My stations in their new order, as order tokens. */
  const moveMyStations = React.useCallback(
    (keys: string[]) => {
      const state = store.getState();
      const current = selectMyWeatherEditorSections(state);
      const byKey = new Map(current.myStations.map((item) => [item.key, item]));
      const myStations = keys.flatMap((key) => byKey.get(key) ?? []);
      saveStationOrder(
        MyWeather.editorStationIds(
          {...current, myStations},
          selectFavoriteStationIds(state),
        ),
      );
    },
    [saveStationOrder, store],
  );

  /** Favorites in their new order, as order tokens. */
  const moveFavorites = React.useCallback(
    (keys: string[]) => {
      const state = store.getState();
      const current = selectMyWeatherEditorSections(state);
      const byKey = new Map(current.favorites.map((item) => [item.key, item]));
      const favorites = keys.flatMap((key) => byKey.get(key) ?? []);
      const stationIds = MyWeather.editorStationIds(
        {...current, favorites},
        selectFavoriteStationIds(state),
      );
      // Under Login As the places and the order are the staff member's own:
      // only the viewed account's station order changes (#420).
      if (getDebugEmail(state)) {
        dispatch(setStationSort(stationIds));
        sendStationIds(stationIds);
        return;
      }
      // Older screens read the favourite places in an order of their own: it
      // follows, every other entry (the GPS one) keeping its slot.
      const hashes = state.location.favoriteLocationHashs;
      const nextHashes = MyWeather.reorder(
        hashes,
        favorites.flatMap((item) =>
          item.kind === 'place' ? [item.place.hash] : [],
        ),
      );
      if (!isEqual(nextHashes, hashes)) {
        dispatch(reorderFavoriteLocations(nextHashes));
      }
      saveOrder(
        MyWeather.reorder(selectMyWeatherOrder(state), keys),
        stationIds,
      );
    },
    [dispatch, saveOrder, sendStationIds, store],
  );

  const removeStationFavorite = React.useCallback(
    ({station}: MyWeather.MyWeatherStationItem) => {
      const restoreFavorite = () => {
        if (!selectFavoriteStationIds(store.getState()).includes(station.id)) {
          dispatch(addFavoriteStation(station.id));
        }
      };
      // A failure answered after a sign-out, an account switch or the end
      // of Login As is not about the favourites shown then.
      const sameSession = sessionOf(store);
      const signedIn = store.getState().user.token != null;
      const stillSignedIn = () => signedIn && sameSession();
      let undone = false;
      // Silent, so the Undo snackbar stays up (a failed token refresh still
      // raises its own: useApiClient ignores `silent` there). Answers whether
      // the account surely dropped the favourite. If not, it comes back here
      // too (unless Undo did that already), and one snackbar says so. After
      // the station's writes already queued (an Undo's add), and not at all
      // once the session has changed: fetch would send it as whoever is
      // signed in then. Signed out there is no account: the favourite is
      // this device's, gone once removed here, and nothing is sent.
      const removal = !signedIn
        ? Promise.resolve(true)
        : queueStationWrite(station.id, () =>
            sameSession()
              ? fetch(WeatherService.removeFavoriteStation(station.id), {
                  silent: true,
                })
              : Promise.reject(new Error('session changed')),
          ).then(
            () => true,
            (error) => {
              // 400: the account held no such favourite. 404: the station is not
              // visible to this user any more (unshared, or deleted), which
              // deletes the favourite with it. Gone either way.
              if (
                error instanceof ApiError &&
                (error.statusCode === 400 || error.statusCode === 404)
              ) {
                return true;
              }
              if (!undone && stillSignedIn()) {
                restoreFavorite();
                dispatch(
                  setAlert({
                    type: 'ERROR',
                    message: formatMessage(
                      messages.stations.removeFromMyWeatherFailed,
                      {name: Station.getStationName(station)},
                    ),
                  }),
                );
              }
              return false;
            },
          );
      dispatch(removeFavoriteStation(station.id));
      trackCoreEvent({name: 'favorite_remove', params: {kind: 'station'}});
      return () => {
        undone = true;
        // Removed signed out, then a sign-in before Undo: the favourite was
        // the device's, and the account never had it. Put back here only,
        // with no add, the next /me would drop it again.
        if (!signedIn && !sameSession()) {
          return;
        }
        restoreFavorite();
        trackCoreEvent({name: 'favorite_add', params: {kind: 'station'}});
        // In the station's chain, after the removal: sent at once, the add
        // could reach the server first and be undone by it, and a Remove
        // made after this Undo waits for it in turn (#419). The account
        // keeps the station's token in its order, so the slot needs nothing
        // more. A removal that failed may still have gone through (a
        // timeout): the add goes silently, since an account that kept the
        // favourite refuses it as a duplicate. Nothing goes once the session
        // has changed (fetch would send it as whoever is signed in then), or
        // the station was removed again meanwhile.
        const add = WeatherService.addFavoriteStation(station.id);
        queueStationWrite(station.id, () =>
          removal.then((dropped) => {
            if (
              !stillSignedIn() ||
              !selectFavoriteStationIds(store.getState()).includes(station.id)
            ) {
              return undefined;
            }
            return dropped ? fetch(add) : fetch(add, {silent: true});
          }),
        ).catch(() => {});
      };
    },
    [dispatch, fetch, formatMessage, store],
  );

  const removePlaceFavorite = React.useCallback(
    ({place}: MyWeather.MyWeatherPlaceItem) => {
      const removedFrom = store.getState();
      const index = removedFrom.location.favoriteLocationHashs.indexOf(
        place.hash,
      );
      // Where the place sat in the My weather order, for Undo: the sync
      // deletes it on the account at once, and the /me that follows brings
      // back an order without it, long before the Undo snackbar has gone.
      const token = MyWeather.placeToken(place.hash);
      const slot = slotOf(selectMyWeatherOrder(removedFrom), token);
      dispatch(removeFavoriteLocation(place.hash));
      trackCoreEvent({name: 'favorite_remove', params: {kind: 'place'}});
      return () => {
        dispatch(restoreFavoriteLocation({location: place, index}));
        trackCoreEvent({name: 'favorite_add', params: {kind: 'place'}});
        // The account dropped the place from its order when it deleted it,
        // and saves it again at the end: the order goes out again, which
        // the order sync holds until the place is saved there. With the
        // station order an edit still waiting recorded, else the editor's:
        // never the device's own, which a /me may have reset. With the
        // sync off nothing would send it (#421): the slot is put back here.
        const state = store.getState();
        const order = selectMyWeatherOrder(state);
        if (order && !getDebugEmail(state)) {
          if (!selectMyWeatherSyncEnabled(state)) {
            dispatch(setMyWeatherOrder(putBack(order, token, slot)));
            return;
          }
          const stationIds =
            selectMyWeatherOrderStationIds(state) ??
            MyWeather.editorStationIds(
              selectMyWeatherEditorSections(state),
              selectFavoriteStationIds(state),
            );
          dispatch(
            editMyWeatherOrder({
              order: putBack(order, token, slot),
              stationIds,
            }),
          );
        }
      };
    },
    [dispatch, store],
  );

  const remove = React.useCallback(
    (item: MyWeather.MyWeatherItem) => {
      // Under Login As the places are the staff member's own: removed, the
      // place would be deleted from their account once Login As ends (#420).
      if (item.kind === 'place' && getDebugEmail(store.getState())) {
        return;
      }
      const name =
        item.kind === 'station'
          ? Station.getStationName(item.station)
          : item.place.title;
      const undo =
        item.kind === 'station'
          ? removeStationFavorite(item)
          : removePlaceFavorite(item);
      let undone = false;
      dispatch(
        setAlert({
          type: 'INFO',
          message: formatMessage(messages.stations.removedFromMyWeather, {
            name,
          }),
          action: {
            label: formatMessage(messages.stations.undoRemove),
            onPress: () => {
              if (!undone) {
                undone = true;
                undo();
              }
            },
          },
        }),
      );
    },
    [
      dispatch,
      formatMessage,
      removePlaceFavorite,
      removeStationFavorite,
      store,
    ],
  );

  /**
   * Hide or Show again, once the account has done it: the row moves group,
   * and the snackbar says so. Not silent: a failure is the app's error
   * snackbar, the row left where it was.
   */
  const setHidden = React.useCallback(
    ({station}: MyWeather.MyWeatherStationItem, hidden: boolean) =>
      fetch(
        hidden
          ? WeatherService.hideLinkedStation(station.id)
          : WeatherService.unhideLinkedStation(station.id),
      )
        .then((data) => {
          dispatch(addStation(data));
          dispatch(
            setAlert({
              type: 'INFO',
              message: formatMessage(
                hidden
                  ? messages.stations.hiddenFromMyWeather
                  : messages.stations.shownInMyWeather,
                {name: Station.getStationName(station)},
              ),
            }),
          );
        })
        .catch(() => {}),
    [dispatch, fetch, formatMessage],
  );

  /** One of your stations, out of My stations and into Hidden. */
  const hide = React.useCallback(
    (item: MyWeather.MyWeatherStationItem) => {
      setHidden(item, true);
    },
    [setHidden],
  );

  const unhide = React.useCallback(
    (item: MyWeather.MyWeatherStationItem) => {
      setHidden(item, false);
    },
    [setHidden],
  );

  return {sections, moveMyStations, moveFavorites, remove, hide, unhide};
};

export default useMyWeatherEditor;
